import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, X, ChevronRight, Globe, Search, FileText, Check, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { toast } from "sonner";

const STEPS = [
  { key: "project", label: "Add your site", icon: Globe },
  { key: "google", label: "Connect Google", icon: Sparkles },
  { key: "audit", label: "First audit", icon: Search },
  { key: "blog", label: "First blog post", icon: FileText },
];

/**
 * OnboardingWizard — full-screen modal that walks new users through the
 * 4-step setup. Renders only when state.completed === false AND no signals
 * indicate the user has already done the work.
 */
export default function OnboardingWizard() {
  const [state, setState] = useState(null);
  const [show, setShow] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/onboarding/state").then(({ data }) => {
      if (cancelled) return;
      setState(data);
      // Show only if not completed and the user genuinely is new
      const signals = data.signals || {};
      const hasDoneAnything = signals.has_project || signals.has_audit || signals.has_blog;
      if (!data.completed && !data.skipped && !hasDoneAnything) {
        setShow(true);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const advance = async (step) => {
    try {
      const { data } = await api.post("/onboarding/advance", { step });
      setState(data);
      if (data.completed) {
        setClosing(true);
        setTimeout(() => setShow(false), 600);
        toast.success("You're all set! Welcome aboard 🎉");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const skip = async () => {
    try {
      await api.post("/onboarding/skip");
    } catch {}
    setShow(false);
  };

  if (!show || !state) return null;
  const current = state.current_step || "project";
  const completedSet = new Set(state.completed_steps || []);

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md grid place-items-center px-4 py-8 overflow-y-auto"
         data-testid="onboarding-wizard"
         style={{ animation: closing ? "fadeOut 0.4s forwards" : "fadeIn 0.3s" }}>
      <div className="relative bg-[#0B0B0E] border border-zinc-800 rounded-sm w-full max-w-2xl">
        <button onClick={skip} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-200 transition-colors" data-testid="onboarding-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        {/* Progress dots */}
        <div className="px-8 pt-8">
          <div className="flex items-center gap-2 mb-6">
            {STEPS.map((s, i) => {
              const isDone = completedSet.has(s.key);
              const isCurrent = s.key === current;
              return (
                <div key={s.key} className="flex items-center gap-2 flex-1">
                  <div className={`w-7 h-7 rounded-full grid place-items-center text-[10px] font-semibold transition-colors ${isDone ? "bg-[#10B981] text-white" : isCurrent ? "bg-[#0055FF] text-white" : "bg-zinc-900 text-zinc-600 border border-zinc-800"}`}>
                    {isDone ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-px ${isDone ? "bg-[#10B981]" : "bg-zinc-800"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="px-8 pb-8">
          {current === "project" && <ProjectStep onDone={() => advance("project")} />}
          {current === "google" && <GoogleStep onDone={() => advance("google")} onSkipStep={() => advance("google")} />}
          {current === "audit" && <AuditStep onDone={() => advance("audit")} />}
          {current === "blog" && <BlogStep onDone={() => advance("blog")} />}
        </div>

        <div className="px-8 py-4 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-600">
          <span>Step {STEPS.findIndex((s) => s.key === current) + 1} of {STEPS.length}</span>
          <button onClick={skip} className="hover:text-zinc-300 transition-colors" data-testid="onboarding-skip-all">
            Skip setup, I'll explore on my own →
          </button>
        </div>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } } @keyframes fadeOut { from { opacity: 1 } to { opacity: 0 } }`}</style>
    </div>
  );
}

/* ============ Step 1 — Add a project ============ */
function ProjectStep({ onDone }) {
  const { refresh } = useProjects();
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [industry, setIndustry] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/projects", { name: name || domain, domain, industry });
      await refresh?.();
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="step-project">
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#3377FF]">Step 1 of 4</span>
      <h2 className="font-display text-2xl font-semibold mt-1 mb-2">Tell us about your site</h2>
      <p className="text-sm text-zinc-400 mb-6">RankPilot needs one website to optimise. You can add more later.</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Website domain</Label>
          <Input value={domain} onChange={(e) => setDomain(e.target.value)} required placeholder="yoursite.com"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="onb-domain" autoFocus />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Friendly name <span className="text-zinc-700 normal-case">(optional)</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My main site"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="onb-name" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Industry <span className="text-zinc-700 normal-case">(optional)</span></Label>
            <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Dental, SaaS, eCommerce…"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="onb-industry" />
          </div>
        </div>
        <Button type="submit" disabled={busy || !domain} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 w-full" data-testid="onb-project-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </form>
    </div>
  );
}

/* ============ Step 2 — Connect Google (skippable) ============ */
function GoogleStep({ onDone, onSkipStep }) {
  const connect = () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${apiBase}/api/google/oauth/start?return_to=${encodeURIComponent("/dashboard?onboarding=audit")}`;
  };
  return (
    <div data-testid="step-google">
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#3377FF]">Step 2 of 4</span>
      <h2 className="font-display text-2xl font-semibold mt-1 mb-2">Connect Google (optional)</h2>
      <p className="text-sm text-zinc-400 mb-6">Link Google Search Console, Analytics, and Google Ads so RankPilot can use your real data instead of estimates. You can do this later.</p>
      <ul className="space-y-2 mb-6 text-sm text-zinc-300">
        <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" /> Real keyword data from your Search Console</li>
        <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" /> Live traffic + conversion metrics from GA4</li>
        <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" /> Push AI-generated campaigns to Google Ads</li>
      </ul>
      <div className="flex gap-2">
        <Button onClick={connect} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 flex-1" data-testid="onb-google-connect">
          Connect Google <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button onClick={onSkipStep} variant="outline" className="bg-zinc-950 border-zinc-800 rounded-sm h-11" data-testid="onb-google-skip">
          Skip for now
        </Button>
      </div>
    </div>
  );
}

/* ============ Step 3 — Run first audit ============ */
function AuditStep({ onDone }) {
  const { active } = useProjects();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${active.id}/audit`);
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="step-audit">
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#3377FF]">Step 3 of 4</span>
      <h2 className="font-display text-2xl font-semibold mt-1 mb-2">Run your first SEO audit</h2>
      <p className="text-sm text-zinc-400 mb-6">
        We'll analyse <code className="text-zinc-300">{active?.domain || "your site"}</code> across 80+ SEO factors. Takes about 30 seconds.
      </p>
      {!result ? (
        <Button onClick={run} disabled={busy || !active} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 w-full" data-testid="onb-audit-run">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Run my first audit</>}
        </Button>
      ) : (
        <div className="space-y-4" data-testid="onb-audit-result">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5 flex items-center gap-4">
            <div className="w-16 h-16 rounded-sm grid place-items-center bg-[#0055FF]/15">
              <span className="font-display text-3xl font-bold text-[#3377FF]">{result.result?.score ?? "—"}</span>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Your SEO Score</div>
              <p className="text-sm text-zinc-300 mt-1 line-clamp-2">{result.result?.summary || "Audit complete."}</p>
            </div>
          </div>
          <Button onClick={onDone} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 w-full" data-testid="onb-audit-next">
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}

/* ============ Step 4 — Generate first blog ============ */
function BlogStep({ onDone }) {
  const { active } = useProjects();
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [post, setPost] = useState(null);

  const run = async (e) => {
    e?.preventDefault();
    if (!active || !topic) return;
    setBusy(true);
    try {
      const { data } = await api.post("/blog/generate", { project_id: active.id, topic, keyword: topic });
      setPost(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="step-blog">
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#3377FF]">Step 4 of 4</span>
      <h2 className="font-display text-2xl font-semibold mt-1 mb-2">Write your first blog post</h2>
      <p className="text-sm text-zinc-400 mb-6">Pick any topic — we'll generate a full SEO-optimised article with meta tags, FAQ, and a featured image.</p>
      {!post ? (
        <form onSubmit={run} className="space-y-4">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Topic / keyword</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="best running shoes for marathons"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="onb-blog-topic" autoFocus required />
          </div>
          <Button type="submit" disabled={busy || !topic} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 w-full" data-testid="onb-blog-run">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating (~30s)…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate my first post</>}
          </Button>
        </form>
      ) : (
        <div className="space-y-4" data-testid="onb-blog-result">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Generated post</div>
            <div className="font-display text-base font-semibold text-zinc-100">{post.title}</div>
            <div className="text-xs text-zinc-500 mt-2 line-clamp-3">{post.meta_description}</div>
          </div>
          <Button onClick={onDone} className="bg-[#10B981] hover:bg-[#0d8a64] rounded-sm h-11 w-full text-white" data-testid="onb-blog-finish">
            Finish setup <Check className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}
